import { useEffect, useState, useRef } from 'react';

const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const Counter = ({ end, duration, suffix = '' }: { end: number; duration: number; suffix?: string }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!isVisible) return;

      let startTime: number;
      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * end));

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }, [isVisible, end, duration]);

    return (
      <span>
        {count}
        {suffix}
      </span>
    );
  };

  const stats = [
    {
      icon: 'ri-time-line',
      value: 15,
      suffix: '+',
      label: 'Years in Business',
      description: 'Proven track record',
    },
    {
      icon: 'ri-team-line',
      value: 500,
      suffix: '+',
      label: 'Active Partners',
      description: 'Worldwide network',
    },
    {
      icon: 'ri-box-3-line',
      value: 50000,
      suffix: '+',
      label: 'Product Range',
      description: 'Components available',
    },
    {
      icon: 'ri-global-line',
      value: 25,
      suffix: '+',
      label: 'Countries Served',
      description: 'International reach',
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-24 px-6 lg:px-12 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
    >
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/20">
            <i className="ri-bar-chart-line text-blue-400"></i>
            <span className="text-sm text-white font-semibold">Our Impact</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Trusted by <strong>Industry Leaders</strong>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Delivering excellence in electronic components supply and logistics services across international markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-blue-400/50 transition-all duration-500 hover:-translate-y-2"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="relative z-10">
                <div className="w-18 h-18 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ width: '4.5rem', height: '4.5rem' }}>
                  <i className={`${stat.icon} text-white text-3xl`}></i>
                </div>

                <div className="text-5xl font-bold text-white mb-2">
                  <Counter end={stat.value} duration={2000} suffix={stat.suffix} />
                </div>

                <h3 className="text-xl font-semibold text-white mb-2">{stat.label}</h3>
                <p className="text-slate-400 text-sm">{stat.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
